import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as forumApi from '../api/forum';
import { usePagedList } from '../lib/usePagedList';
import { useAuth } from '../lib/auth';
import { useCategoryModerator } from '../lib/forumModeration';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import Modal from '../components/Modal';
import {
  ErrorAlert,
  Spinner,
  TextareaField,
  PagedListFooter,
  RelativeTime,
  UserChip,
} from '../components/ui';
import { cn, ui } from '../lib/ui';

const POSTS_PAGE_SIZE = 20;

export default function ForumThread() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user, isStaff } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();

  const [reply, setReply] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);

  const [editingPost, setEditingPost] = useState(null); // the post object being edited, or null
  const [editContent, setEditContent] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState(null);

  const [deletingId, setDeletingId] = useState(null);
  // Moderation (FEA-19): lock/pin/delete return the full updated thread, so
  // they're applied directly here rather than round-tripping through
  // usePagedList's `extra` (which has no public mutator — only its items do).
  const [thread, setThread] = useState(null);
  const [modBusy, setModBusy] = useState(null); // 'lock' | 'pin' | 'delete' | null

  const list = usePagedList(
    cursor =>
      forumApi
        .getThread(threadId, { limit: POSTS_PAGE_SIZE, lastEvaluatedKey: cursor })
        .then(d => ({
          items: d.posts || [],
          lastEvaluatedKey: d.lastEvaluatedKey,
          extra: d.thread,
        })),
    [threadId]
  );

  useEffect(() => {
    setThread(list.extra);
  }, [list.extra]);

  // FEA-30: moderation powers widen from staff-only to staff OR a member
  // designated a moderator of this thread's category (docs/FRONTEND-PLAN.md
  // §4.3). The thread payload has no category — see lib/forumModeration.js.
  const { isModerator } = useCategoryModerator(thread?.categoryId, {
    userId: user?.userId,
    isStaff,
  });

  async function onReply(event) {
    event.preventDefault();
    setReplyBusy(true);
    try {
      await forumApi.createPost(threadId, { content: reply });
      setReply('');
      toast.success('Reply posted.');
      list.reload();
    } catch (err) {
      toast.error(err);
    } finally {
      setReplyBusy(false);
    }
  }

  function openEdit(post) {
    setEditingPost(post);
    setEditContent(post.content || '');
    setEditError(null);
  }

  function closeEdit() {
    if (editBusy) return;
    setEditingPost(null);
  }

  async function saveEdit(event) {
    event.preventDefault();
    if (!editingPost) return;
    setEditBusy(true);
    setEditError(null);
    try {
      const data = await forumApi.updatePost(editingPost.postId, { content: editContent });
      list.mutateItem(
        editingPost.postId,
        { content: data.post.content, editedAt: data.post.editedAt },
        'postId'
      );
      toast.success('Post updated.');
      setEditingPost(null);
    } catch (err) {
      setEditError(err);
      toast.error(err);
    } finally {
      setEditBusy(false);
    }
  }

  async function onDelete(post) {
    const ok = await confirm({
      title: 'Delete post?',
      message: 'This cannot be undone. The post will be replaced with a "deleted" placeholder.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setDeletingId(post.postId);
    try {
      await forumApi.deletePost(post.postId);
      list.mutateItem(post.postId, { isDeleted: true, content: null }, 'postId');
      toast.success('Post deleted.');
    } catch (err) {
      toast.error(err);
    } finally {
      setDeletingId(null);
    }
  }

  async function toggleLock() {
    if (!thread) return;
    setModBusy('lock');
    try {
      const { thread: updated } = thread.isLocked
        ? await forumApi.unlockThread(threadId)
        : await forumApi.lockThread(threadId);
      setThread(updated);
      toast.success(updated.isLocked ? 'Thread locked.' : 'Thread unlocked.');
    } catch (err) {
      toast.error(err);
    } finally {
      setModBusy(null);
    }
  }

  async function togglePin() {
    if (!thread) return;
    setModBusy('pin');
    try {
      const { thread: updated } = thread.isPinned
        ? await forumApi.unpinThread(threadId)
        : await forumApi.pinThread(threadId);
      setThread(updated);
      toast.success(updated.isPinned ? 'Thread pinned.' : 'Thread unpinned.');
    } catch (err) {
      toast.error(err);
    } finally {
      setModBusy(null);
    }
  }

  async function onDeleteThread() {
    if (!thread) return;
    const ok = await confirm({
      title: 'Delete thread?',
      message:
        'This cannot be undone. The thread and its posts will disappear from category listings.',
      confirmLabel: 'Delete thread',
      danger: true,
    });
    if (!ok) return;
    setModBusy('delete');
    try {
      await forumApi.deleteThread(threadId);
      toast.success('Thread deleted.');
      navigate(`/forum/categories/${encodeURIComponent(thread.categoryId)}`);
    } catch (err) {
      toast.error(err);
      setModBusy(null);
    }
  }

  const lockedForMembers = Boolean(thread && thread.isLocked && !isModerator);

  return (
    <>
      <p>
        <Link to="/forum">← Forum</Link>
      </p>
      <ErrorAlert error={list.error} />

      {list.loading ? (
        <Spinner />
      ) : !thread ? null : (
        <>
          <h1 className={ui.h1}>
            {thread.title}
            {thread.isPinned && <span className={ui.pillInfo}> Pinned</span>}
            {thread.isLocked && <span className={ui.pillNeutral}> Locked</span>}
          </h1>

          {isModerator && (
            <div className={cn(ui.row, 'mb-4')}>
              <button
                type="button"
                className={ui.btnSecondary}
                disabled={Boolean(modBusy)}
                onClick={togglePin}
              >
                {modBusy === 'pin' ? 'Working…' : thread.isPinned ? 'Unpin' : 'Pin'}
              </button>
              <button
                type="button"
                className={ui.btnSecondary}
                disabled={Boolean(modBusy)}
                onClick={toggleLock}
              >
                {modBusy === 'lock' ? 'Working…' : thread.isLocked ? 'Unlock' : 'Lock'}
              </button>
              <button
                type="button"
                className={ui.btnDanger}
                disabled={Boolean(modBusy)}
                onClick={onDeleteThread}
              >
                {modBusy === 'delete' ? 'Deleting…' : 'Delete thread'}
              </button>
            </div>
          )}

          {list.items.map(p => (
            <section key={p.postId} className={cn(ui.card, 'mb-4')}>
              <p className={cn(ui.muted, 'text-body-s')}>
                <UserChip userId={p.authorId} name={p.authorName} /> —{' '}
                <RelativeTime iso={p.createdAt} />
                {!p.isDeleted && p.editedAt && (
                  <>
                    {' '}
                    (edited <RelativeTime iso={p.editedAt} />)
                  </>
                )}
              </p>
              {p.isDeleted ? (
                <p className={ui.muted}>This post was deleted.</p>
              ) : (
                <p className="whitespace-pre-wrap">{p.content}</p>
              )}
              {!p.isDeleted && (p.authorId === user?.userId || isModerator) && (
                <div className={ui.row}>
                  {p.authorId === user?.userId && (
                    <button type="button" className={ui.btnSecondary} onClick={() => openEdit(p)}>
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    className={ui.btnDanger}
                    disabled={deletingId === p.postId}
                    onClick={() => onDelete(p)}
                  >
                    {deletingId === p.postId ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              )}
            </section>
          ))}

          <PagedListFooter
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            label="Load more replies"
            endLabel={list.items.length ? 'No more replies.' : null}
          />

          {lockedForMembers ? (
            <p className={cn(ui.muted, 'mt-4')}>
              This thread is locked — new replies are disabled.
            </p>
          ) : (
            <section className={cn(ui.card, 'mt-4')}>
              <h3 className={ui.h3}>Reply</h3>
              {thread.isLocked && (
                <p className={cn(ui.muted, 'text-body-s')}>
                  This thread is locked for members — as a moderator here, your reply will still
                  post.
                </p>
              )}
              <form onSubmit={onReply}>
                <TextareaField
                  label="Your reply"
                  rows={4}
                  required
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                />
                <button className={ui.btnPrimary} type="submit" disabled={replyBusy}>
                  {replyBusy ? 'Posting…' : 'Post reply'}
                </button>
              </form>
            </section>
          )}
        </>
      )}

      <Modal
        open={Boolean(editingPost)}
        onClose={closeEdit}
        title="Edit post"
        footer={
          <>
            <button
              type="button"
              className={ui.btnSecondary}
              onClick={closeEdit}
              disabled={editBusy}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-post-form"
              className={ui.btnPrimary}
              disabled={editBusy}
            >
              {editBusy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <ErrorAlert error={editError} />
        <form id="edit-post-form" onSubmit={saveEdit}>
          <TextareaField
            label="Message"
            rows={5}
            required
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
          />
        </form>
      </Modal>
    </>
  );
}
